<template>
	<div>
		<v-container>
			<div :class="['text-h6', 'pt-0', 'pb-4', 'text-center']">
				{{ $t('titles.issuingCertificate') }}
			</div>
			<v-row>
				<!-- kandidati -->
				<v-col cols="4" class="pr-0">
					<div>
						<v-row class="justify-space-between">
							<v-col cols="9">
								<div :class="['text-h6', 'pl-2']">
									{{ $t('titles.candidates') }}
								</div>
							</v-col>
							<v-col cols="3" class="d-flex justify-left">
								<v-tooltip bottom>
									<template v-slot:activator="{ on, attrs }">
										<v-btn icon v-bind="attrs" @click="addCandidateBtnClick" v-on="on">
											<v-icon>fas fa-circle-plus</v-icon>
										</v-btn>
									</template>
									<span>{{ $t('tooltips.add') }}</span>
								</v-tooltip>
							</v-col>
						</v-row>
						<v-divider></v-divider>
						<v-container v-if="candidates.length > 0" class="overflow-container">
							<v-row v-for="(item, key) in candidates" :key="key" class="row-border mb-1">
								<div class="bordered-col d-flex justify-space-between">
									<div :class="['text-h8', 'pa-2']">{{ item.name }}</div>
									<div>
										<v-tooltip bottom>
											<template v-slot:activator="{ on, attrs }">
												<v-btn icon v-bind="attrs" @click="deleteCandidate(item.id)" v-on="on">
													<v-icon small>fa-trash</v-icon>
												</v-btn>
											</template>
											<span>{{ $t('tooltips.delete') }}</span>
										</v-tooltip>
									</div>
								</div>
								<v-col cols="12" class="pt-0">
									<InputField
										v-model="item.certificate_number"
										:errorBorder="!item.certificate_number_validation"
										:label="'* ' + $t('fields.certificateNumber')"
									></InputField>
								</v-col>
							</v-row>
						</v-container>
						<v-container v-else>
							<v-col cols="12">
								<div :class="['text-h8', 'pa-2', 'd-flex', 'justify-center']">
									{{ $t('titles.noCandidates') }}
								</div>
							</v-col>
						</v-container>
					</div>
				</v-col>
				<!-- forma za unos -->
				<v-col cols="8">
					<v-card flat outlined>
						<v-card-text>
							<!-- tečaj -->
							<v-row>
								<v-col cols="12">
									<div :class="['text-h6']">
										{{ $t('titles.course') }}
									</div>
								</v-col>
								<v-col cols="6">
									<Dropdown2
										v-model="issuedCertificate.planned_course_id"
										:ajax="true"
										:clearable="true"
										:data="'planned-courses'"
										:errorBorder="!issuedCertificateValidation.planned_course_id"
										:label="$t('fields.plannedCourse')"
										@item-selected="handleSelectedPlannedCourse"
									></Dropdown2>
								</v-col>
								<v-col cols="6">
									<DatePicker
										v-model="issuedCertificate.start_date"
										:errorBorder="!issuedCertificateValidation.start_date"
										:date-label="'* ' + $t('fields.startDate')"
									></DatePicker>
								</v-col>
								<v-col cols="6">
									<Dropdown2
										v-model="issuedCertificate.office_id"
										:ajax="true"
										:data="'offices'"
										:errorBorder="!issuedCertificateValidation.office_id"
										:label="'* ' + $t('fields.office')"
										@item-selected="handleSelectedOffice"
									></Dropdown2>
								</v-col>
								<v-col cols="6">
									<DatePicker
										v-model="issuedCertificate.end_date"
										:errorBorder="!issuedCertificateValidation.end_date"
										:date-label="'* ' + $t('fields.endDate')"
									></DatePicker>
								</v-col>
								<v-col cols="12">
									<InputField
										v-model="issuedCertificate.held_in"
										:errorBorder="!issuedCertificateValidation.held_in"
										:label="'* ' + $t('fields.heldIn')"
									></InputField>
								</v-col>
							</v-row>
							<!-- certifikat -->
							<v-row>
								<v-col cols="12">
									<div :class="['text-h6']">
										{{ $t('titles.certificate') }}
									</div>
								</v-col>
								<v-col cols="6">
									<Dropdown2
										v-model="issuedCertificate.certificate_id"
										:ajax="true"
										:data="'certificates'"
										:errorBorder="!issuedCertificateValidation.certificate_id"
										:label="'* ' + $t('fields.certificate')"
										@item-selected="getCertificate"
									></Dropdown2>
								</v-col>
								<v-col cols="6">
									<DatePicker
										v-model="issuedCertificate.issue_date"
										:errorBorder="!issuedCertificateValidation.issue_date"
										:date-label="'* ' + $t('fields.issueDate')"
									></DatePicker>
								</v-col>
								<v-col cols="6">
									<InputField
										v-model="issuedCertificate.issued_in"
										:errorBorder="!issuedCertificateValidation.issued_in"
										:label="'* ' + $t('fields.issuedIn')"
									></InputField>
								</v-col>
								<v-col cols="6">
									<DatePicker
										:disabled="issuedCertificate.unlimited_validity"
										v-model="issuedCertificate.expiration_date"
										:errorBorder="!issuedCertificateValidation.expiration_date"
										:date-label="'* ' + $t('fields.expirationDate')"
									></DatePicker>
								</v-col>
								<v-col cols="12">
									<CheckBox
										v-model="issuedCertificate.unlimited_validity"
										:label="$t('fields.unlimitedValidity')"
									></CheckBox>
								</v-col>
								<v-col cols="12">
									<TextArea
										v-model="issuedCertificate.provisions"
										:errorBorder="!issuedCertificateValidation.provisions"
										:label="$t('fields.provisions')"
										rows="3"
									></TextArea>
								</v-col>
								<v-row>
									<v-col cols="4">
										<div :class="['text-body-1', 'pt-5', 'pl-2']">
											{{ $t('labels.certificateType') }}
										</div>
									</v-col>
									<v-col cols="8">
										<v-radio-group v-model="issuedCertificate.certificate_type" row mandatory>
											<v-radio :label="$t('fields.twoSignings')" value="dva_potpisa"></v-radio>
											<v-radio :label="$t('fields.threeSignings')" value="tri_potpisa"></v-radio>
										</v-radio-group>
									</v-col>
								</v-row>
							</v-row>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
			<v-row>
				<v-col cols="12" class="d-flex justify-center">
					<v-btn class="ma-2" color="primary" variant="text" @click="saveClick">
						{{ $t('buttons.issueCertificate') }}
					</v-btn>
					<v-btn class="ma-2" color="secondary" variant="text" @click="previewClick">
						{{ $t('buttons.preview') }}
					</v-btn>
					<v-btn class="ma-2" color="basicButton" variant="text" @click="cancelClick">
						{{ $t('buttons.cancel') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
		<Modal :show-modal="showModal" :max-width="600">
			<v-row>
				<v-col>
					<Dropdown2
						v-model="candidate"
						:ajax="true"
						:data="'candidates'"
						:errorBorder="!candidateValidation"
						:label="'* ' + $t('fields.candidate')"
						@item-selected="handleSelectedCandidate"
					></Dropdown2>
				</v-col>
			</v-row>
			<v-row>
				<v-col cols="12" class="d-flex justify-center">
					<v-btn class="ma-2" color="primary" variant="text" @click="addCandidate">
						{{ $t('buttons.add') }}
					</v-btn>
					<v-btn class="ma-2" color="basicButton" variant="text" @click="cancelClickCandidateModal">
						{{ $t('buttons.cancel') }}
					</v-btn>
				</v-col>
			</v-row>
		</Modal>
	</div>
</template>

<script>
import InputField from '../../trinkets/InputField';
import CheckBox from '../../trinkets/CheckBox';
import Dropdown2 from '../../trinkets/Dropdown2';
import Modal from '../../trinkets/Modal';
import TextArea from '../../trinkets/TextArea';
import DatePicker from '../../trinkets/DatePicker';
import IssuedCertificate from '../../../models/IssuedCertificate';

export default {
	name: 'IssuedCertificate',
	components: { DatePicker, InputField, Dropdown2, TextArea, Modal, CheckBox },
	emits: ['close', 'reloadIssuedCertificates'],
	data() {
		return {
			issuedCertificate: new IssuedCertificate({ id: 0, issue_date: new Date().toISOString().slice(0, 10) }),
			issuedCertificateValidation: new IssuedCertificate({}, true),
			candidates: [],
			newCandidate: {},
			selectedPlannedCourseId: null,
			candidate: null,
			candidateValidation: true,
			showModal: false,
			certificateValidityMonths: 0,
			test: null,
			testVal: true,
		};
	},
	watch: {
		'issuedCertificate.issue_date': function (newVal) {
			if (this.issuedCertificate.expiration_date != null) {
				this.issuedCertificate.expiration_date = this.setExpirationDate(newVal, this.certificateValidityMonths);
			}
		},
		'issuedCertificate.unlimited_validity': function (newVal) {
			if (newVal) {
				this.issuedCertificate.expiration_date = null;
			}
		},
	},
	methods: {
		addCandidateBtnClick() {
			this.showModal = true;
		},
		cancelClickCandidateModal() {
			this.showModal = false;
		},
		cancelClick() {
			this.$emit('close');
		},
		handleSelectedPlannedCourse(item) {
			this.selectedPlannedCourseId = item.id;
			this.getSelectedPlannedCourse();
		},
		setExpirationDate(originalDate, validity_months) {
			const original = new Date(originalDate);
			original.setMonth(original.getMonth() + validity_months);
			return original.toISOString().split('T')[0];
		},
		addCandidate() {
			if (this.candidate == null) {
				this.candidateValidation = false;
			} else {
				this.candidates.push(this.newCandidate);
				this.candidateValidation = true;
				this.showModal = false;
				this.candidate = null;
				this.newCandidate = {};
			}
		},
		deleteCandidate(id) {
			this.candidates = this.candidates.filter((item) => item.id !== id);
		},
		handleSelectedCandidate(candidate) {
			this.newCandidate.id = candidate.id;
			this.newCandidate.name = candidate.name;
			this.newCandidate.certificate_number = null;
			this.newCandidate.certificate_number_validation = true;
		},
		handleSelectedOffice(office) {
			this.issuedCertificate.held_in = office.name + ', Croatia';
		},
		getSelectedPlannedCourse() {
			var vm = this;
			this.candidates = [];
			this.$APICall(
				'get',
				'issued-certificates/' + this.selectedPlannedCourseId,
				{},
				(data, success) => {
					if (success) {
						let usableData = JSON.parse(JSON.stringify(data));
						vm.issuedCertificate = usableData;
						vm.getEnrolledPreregistrations();
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				},
				false
			);
		},
		getCertificate(certificate) {
			var vm = this;
			this.$APICall(
				'get',
				'certificates/' + certificate.id,
				{},
				(data, success) => {
					if (success) {
						let usableData = JSON.parse(JSON.stringify(data));
						vm.issuedCertificate.provisions = usableData.provisions;
						vm.certificateValidityMonths = usableData.validity;
						vm.issuedCertificate.unlimited_validity = usableData.unlimited_validity;
						if (!usableData.unlimited_validity) {
							vm.issuedCertificate.expiration_date = vm.setExpirationDate(
								vm.issuedCertificate.issue_date,
								vm.certificateValidityMonths
							);
						}
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				},
				false
			);
		},
		getEnrolledPreregistrations() {
			var vm = this;
			this.$APICall(
				'get',
				'preregistrations-enrolled-candidates/' + this.selectedPlannedCourseId,
				{},
				(data, success) => {
					if (success) {
						vm.candidates = data;
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				},
				false
			);
		},
		previewClick() {
			var vm = this;
			if (this.candidates.length > 0) {
				let payload = {
					issued_certificate: this.issuedCertificate,
					candidateId: this.candidates[0]['id'],
				};
				this.$APICall2(
					'post',
					'issued-certificates-preview',
					payload,
					(data, success) => {
						if (success) {
							const blob = new Blob([data], { type: 'application/pdf' });
							const url = URL.createObjectURL(blob);
							const link = document.createElement('a');
							link.href = url;
							link.download = 'predpregled_certifikata.pdf';
							this.downloadingPdf = false;
							link.click();
							URL.revokeObjectURL(url);
						} else {
							vm.$alert(vm.$t('alerts.basicError'));
						}
					},
					false
				);
			} else {
				vm.$alert(vm.$t('alerts.addAtLeastOneCandidate'));
			}
		},
		saveClick() {
			let success = true;
			let errorMessages = '';

			if (!this.issuedCertificate.certificate_id) {
				success = false;
				this.issuedCertificateValidation.certificate_id = false;
			} else {
				this.issuedCertificateValidation.certificate_id = true;
			}

			if (!this.issuedCertificate.office_id) {
				success = false;
				this.issuedCertificateValidation.office_id = false;
			} else {
				this.issuedCertificateValidation.office_id = true;
			}

			if (!this.issuedCertificate.start_date) {
				success = false;
				this.issuedCertificateValidation.start_date = false;
			} else {
				this.issuedCertificateValidation.start_date = true;
			}

			if (!this.issuedCertificate.end_date) {
				success = false;
				this.issuedCertificateValidation.end_date = false;
			} else {
				this.issuedCertificateValidation.end_date = true;
			}

			if (!this.issuedCertificate.issued_in) {
				success = false;
				this.issuedCertificateValidation.issued_in = false;
			} else {
				if (this.issuedCertificate.issued_in.length > 30) {
					this.issuedCertificateValidation.issued_in = false;
					errorMessages +=
						this.$t('alerts.lengthError')
							.replace('%field%', this.$t('fields.issuedIn'))
							.replace('%length%', '30') + '<br />';
				} else {
					this.issuedCertificateValidation.issued_in = true;
				}
			}

			if (this.issuedCertificate.held_in == null || this.issuedCertificate.held_in == '') {
				success = false;
				this.issuedCertificateValidation.held_in = false;
			} else {
				if (this.issuedCertificate.held_in.length > 50) {
					this.issuedCertificateValidation.held_in = false;
					errorMessages +=
						this.$t('alerts.lengthError')
							.replace('%field%', this.$t('fields.heldIn'))
							.replace('%length%', '50') + '<br />';
				} else {
					this.issuedCertificateValidation.held_in = true;
				}
			}

			if (!this.issuedCertificate.issue_date) {
				success = false;
				this.issuedCertificateValidation.issue_date = false;
			} else {
				this.issuedCertificateValidation.issue_date = true;
			}

			if (!this.issuedCertificate.expiration_date && !this.issuedCertificate.unlimited_validity) {
				success = false;
				this.issuedCertificateValidation.expiration_date = false;
			} else {
				this.issuedCertificateValidation.expiration_date = true;
			}

			if (
				this.issuedCertificate.provisions != null &&
				this.issuedCertificate.provisions != '' &&
				this.issuedCertificate.provisions.length > 750
			) {
				this.issuedCertificateValidation.provisions = false;
				errorMessages +=
					this.$t('alerts.lengthError')
						.replace('%field%', this.$t('fields.issuedIn'))
						.replace('%length%', '750') + '<br />';
			} else {
				this.issuedCertificateValidation.provisions = true;
			}

			if (this.candidates.length <= 0) {
				success = false;
				errorMessages += this.$t('alerts.candidateError') + '<br />';
				this.issuedCertificateValidation.candidate_id = false;
			} else {
				for (const candidate of this.candidates) {
					if (candidate.certificate_number === null || candidate.certificate_number === '') {
						candidate.certificate_number_validation = false;
						success = false;
					} else {
						if (candidate.certificate_number.length > 20) {
							candidate.certificate_number_validation = false;
							errorMessages +=
								this.$t('alerts.lengthError')
									.replace('%field%', this.$t('fields.certificateNumber'))
									.replace('%length%', '20') + '<br />';
						} else {
							candidate.certificate_number_validation = true;
						}
					}
				}
				this.issuedCertificateValidation.candidate_id = true;
			}

			if (errorMessages !== '') {
				this.$alert(errorMessages);
				success = false;
			}

			if (success) {
				let vm = this;
				this.issuedCertificate.preregistration_id = 1;
				let payload = {
					issued_certificate: this.issuedCertificate,
					candidates: this.candidates,
				};
				this.$APICall('post', 'issued-certificates', payload, (data, success) => {
					if (success) {
						if (vm.issuedCertificate.id == 0) {
							vm.$notify(vm.$t('alerts.basicAdd'));
						} else {
							vm.$notify(vm.$t('alerts.basicUpdate'));
						}
						vm.$modalBus.$emit('reloadIssuedCertificates');
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
					this.$emit('close');
				});
			}
		},
	},
};
</script>

<style scoped>
.bordered-col {
	padding: 4px;
	width: 100%;
	margin-top: 4px;
}

.row-border {
	border: 1px solid #e0e0e0;
	border-radius: 4px;
}

.overflow-container {
	max-height: 650px;
	overflow-y: auto;
}
</style>
